import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  buildPricingPatch,
  isDurationBasedService,
  pricingFormFromConfig,
  pricingStatusClass,
  pricingStatusLabel,
} from '@/lib/pricingConfig';

function FeeTypeSelect({ label, typeValue, onTypeChange, valueValue, onValueChange, testIdPrefix }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Select value={typeValue} onValueChange={onTypeChange}>
          <SelectTrigger data-testid={`${testIdPrefix}-type`}>
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Fixed</SelectItem>
            <SelectItem value="percentage">Percentage</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid={`${testIdPrefix}-value`}
          type="number"
          min="0"
          value={valueValue}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Value"
        />
      </div>
    </div>
  );
}

function GenericAdminServicePricing() {
  const { serviceKey } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [pricingComplete, setPricingComplete] = useState(false);
  const [incompleteReasons, setIncompleteReasons] = useState([]);
  const [form, setForm] = useState(pricingFormFromConfig());

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/services/${serviceKey}/pricing`);
      setLabel(data.label || serviceKey);
      setPricingComplete(Boolean(data.pricing_complete));
      setIncompleteReasons(data.pricing_incomplete_reasons || []);
      setForm(pricingFormFromConfig({ ...data.pricing_config, service_key: serviceKey }));
    } catch {
      toast.error('Could not load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [serviceKey]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateBooking = (key, value) =>
    setForm((prev) => ({
      ...prev,
      booking_requirements: { ...prev.booking_requirements, [key]: value },
    }));

  const updateDurationOption = (index, field, value) => {
    setForm((prev) => {
      const options = [...(prev.booking_requirements?.duration_options || [])];
      options[index] = { ...options[index], [field]: value };
      return {
        ...prev,
        booking_requirements: { ...prev.booking_requirements, duration_options: options },
      };
    });
  };

  const durationBased = isDurationBasedService(serviceKey);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/services/${serviceKey}/pricing`,
        buildPricingPatch(form),
      );
      setPricingComplete(Boolean(data.pricing?.pricing_complete));
      setIncompleteReasons(data.pricing?.pricing_incomplete_reasons || []);
      toast.success('Pricing saved');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map((d) => d.msg || d).join(', '));
      } else {
        toast.error(detail || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MobileShell title="Pricing">
        <div className="p-5 text-sm text-slate-500">Loading pricing…</div>
      </MobileShell>
    );
  }

  return (
    <MobileShell title={`${label} Pricing`}>
      <div className="px-5 py-5 space-y-4" data-testid="admin-pricing-form">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase">Status</span>
          <span
            data-testid="pricing-status-badge"
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${pricingStatusClass({ pricing_complete: pricingComplete })}`}
          >
            {pricingStatusLabel({ pricing_complete: pricingComplete })}
          </span>
        </div>

        {!pricingComplete && incompleteReasons.length > 0 && (
          <ul className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3 space-y-1" data-testid="pricing-incomplete-reasons">
            {incompleteReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}

        <div className="resqly-card p-4 space-y-3">
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">Currency</Label>
            <Input data-testid="pricing-currency" value={form.currency} disabled />
          </div>
          {!durationBased && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Base price</Label>
              <Input
                data-testid="pricing-base-price"
                type="number"
                min="0"
                value={form.base_price}
                onChange={(e) => update('base_price', e.target.value)}
              />
            </div>
          )}
          {durationBased && (
            <p className="text-xs text-slate-500">
              Duration-based pricing — service price comes from duration options below.
            </p>
          )}
        </div>

        {durationBased && (
          <div className="resqly-card p-4 space-y-3" data-testid="admin-booking-requirements">
            <div className="text-sm font-semibold text-slate-800">Booking requirements</div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-600">Duration options</Label>
              {(form.booking_requirements?.duration_options || []).map((opt, idx) => (
                <div key={opt.id} className="grid grid-cols-[auto_1fr_1fr] gap-2 items-center text-xs">
                  <Checkbox
                    data-testid={`duration-enabled-${opt.id}`}
                    checked={Boolean(opt.enabled)}
                    onCheckedChange={(checked) => updateDurationOption(idx, 'enabled', checked === true)}
                  />
                  <span className="min-w-0">
                    {opt.label}
                    {opt.stay_included ? ' + food + stay' : opt.food_included ? ' + food' : ''}
                  </span>
                  <Input
                    data-testid={`duration-price-${opt.id}`}
                    type="number"
                    min="0"
                    inputMode="numeric"
                    placeholder="₹ Price"
                    value={opt.price}
                    onChange={(e) => updateDurationOption(idx, 'price', e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="resqly-card p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Travel</div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">Included travel km</Label>
            <Input
              data-testid="pricing-included-travel-km"
              type="number"
              min="0"
              value={form.included_travel_km}
              onChange={(e) => update('included_travel_km', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">Travel rate / km</Label>
            <Input
              data-testid="pricing-travel-rate"
              type="number"
              min="0"
              value={form.travel_rate_per_km}
              onChange={(e) => update('travel_rate_per_km', e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="round-trip"
              data-testid="pricing-round-trip"
              checked={form.round_trip}
              onCheckedChange={(checked) => update('round_trip', checked === true)}
            />
            <Label htmlFor="round-trip" className="text-sm text-slate-700">Round-trip travel</Label>
          </div>
        </div>

        <div className="resqly-card p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Service</div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">Included duration (minutes)</Label>
            <Input
              data-testid="pricing-duration"
              type="number"
              min="1"
              value={form.included_duration_minutes}
              onChange={(e) => update('included_duration_minutes', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">Free buffer (minutes)</Label>
            <Input
              data-testid="pricing-buffer"
              type="number"
              min="0"
              value={form.free_buffer_minutes}
              onChange={(e) => update('free_buffer_minutes', e.target.value)}
            />
          </div>
        </div>

        <div className="resqly-card p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Extra time</div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">Billing block (minutes)</Label>
            <Input
              data-testid="pricing-billing-block"
              type="number"
              min="1"
              value={form.billing_block_minutes}
              onChange={(e) => update('billing_block_minutes', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">Price per block</Label>
            <Input
              data-testid="pricing-price-per-block"
              type="number"
              min="0"
              value={form.price_per_block}
              onChange={(e) => update('price_per_block', e.target.value)}
            />
          </div>
        </div>

        <div className="resqly-card p-4 space-y-3">
          <FeeTypeSelect
            label="Convenience fee"
            testIdPrefix="pricing-convenience-fee"
            typeValue={form.convenience_fee_type}
            onTypeChange={(v) => update('convenience_fee_type', v)}
            valueValue={form.convenience_fee_value}
            onValueChange={(v) => update('convenience_fee_value', v)}
          />
          <FeeTypeSelect
            label="Commission"
            testIdPrefix="pricing-commission"
            typeValue={form.commission_type}
            onTypeChange={(v) => update('commission_type', v)}
            valueValue={form.commission_value}
            onValueChange={(v) => update('commission_value', v)}
          />
          <FeeTypeSelect
            label="Tax / GST"
            testIdPrefix="pricing-tax"
            typeValue={form.tax_type}
            onTypeChange={(v) => update('tax_type', v)}
            valueValue={form.tax_value}
            onValueChange={(v) => update('tax_value', v)}
          />
          <p className="text-xs text-slate-500">
            Tax percentage applies to extra service charges as well. 0% is valid.
          </p>
        </div>

        <Button
          data-testid="pricing-save"
          className="w-full bg-slate-900 hover:bg-slate-800"
          disabled={saving}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save Pricing'}
        </Button>

        <Link
          to="/admin/services"
          className="block text-center text-sm text-blue-700 font-semibold py-2"
        >
          Back to Services
        </Link>
      </div>
    </MobileShell>
  );
}


export default function AdminServicePricing() {
  const { serviceKey } = useParams();
  if (serviceKey === 'pharmacy') return <AdminPharmacyPricing />;
  return <GenericAdminServicePricing />;
}
